import {
  DataMessageTypeEnum,
  type MentionEntity,
  type MessageSegment,
  type TerraUIMessage,
  type TerraUIMessageDataPartWithType,
  type TerraUIMessagePart,
  TerraUIMessageDataPartName,
} from '@terra/shared-dto-chat-types';
import { buildMentionDisplaySegments } from '../editor/lexical/plugins/buildMessageSegments';
import type { ObjectContextKind } from '@terra/shared-dto-chat-types';

export const REF_PATTERN = /\[ref: see \*mention \d+\*[^\]]*\]/g;

const TERRA_MENTION_BLOCK_PATTERN =
  /<!-- TERRA_MENTION_START -->[\s\S]*?<!-- TERRA_MENTION_END -->/g;

type MentionsDataPart =
  TerraUIMessageDataPartWithType<DataMessageTypeEnum.Mentions>;

function isMentionsDataPart(
  part: TerraUIMessagePart,
): part is MentionsDataPart {
  return (
    part.type === TerraUIMessageDataPartName &&
    part.data.type === DataMessageTypeEnum.Mentions
  );
}

export function stripRichCardMetadataFromMessageText(text: string): string {
  return text
    .replace(TERRA_MENTION_BLOCK_PATTERN, '')
    .replace(REF_PATTERN, '')
    .trim();
}

export function sanitizeMessageSegmentsForDisplay(
  segments: MessageSegment[],
): MessageSegment[] {
  const sanitized: MessageSegment[] = [];

  for (const segment of segments) {
    if (segment.type === 'mention') {
      sanitized.push(segment);
      continue;
    }

    const value = stripRichCardMetadataFromMessageText(segment.value);

    if (value) {
      sanitized.push({ type: 'text', value });
    }
  }

  return sanitized;
}

export function formatMessageSegmentsAsPlainText({
  segments,
}: {
  segments: MessageSegment[];
}): string {
  return segments
    .map((segment) => {
      if (segment.type === 'mention') {
        return `@${segment.label}`;
      }

      return segment.value.replace(REF_PATTERN, '');
    })
    .join('')
    .trim();
}

const OBJECT_CONTEXT_KINDS = [
  'Finding',
  'Signal',
  'EndPoint',
  'WebPage',
  'Trace',
  'Skill',
  'SourceCode',
  'Chat',
] as const satisfies readonly ObjectContextKind[];

const OBJECT_CONTEXT_MENTION_IN_TEXT_PATTERN = new RegExp(
  `@(${OBJECT_CONTEXT_KINDS.join('|')}) · ([^\n]+)`,
  'g',
);

function extractMentionSegmentsFromPlainText(
  plainText: string,
): MessageSegment[] | null {
  const segments: MessageSegment[] = [];
  let lastIndex = 0;

  for (const match of plainText.matchAll(
    OBJECT_CONTEXT_MENTION_IN_TEXT_PATTERN,
  )) {
    const fullMatch = match[0];
    const matchIndex = match.index;

    if (!fullMatch || matchIndex === undefined) {
      continue;
    }

    const objectKind = match[1] as ObjectContextKind;
    const labelSuffix = match[2]?.trim() ?? '';
    const label = `${objectKind} · ${labelSuffix}`;

    const textBefore = plainText.slice(lastIndex, matchIndex);

    if (textBefore) {
      segments.push({ type: 'text', value: textBefore });
    }

    segments.push({
      type: 'mention',
      entityId: `plain-text:${objectKind}:${labelSuffix}`,
      entityType: 'ObjectContext',
      label,
    });
    lastIndex = matchIndex + fullMatch.length;
  }

  if (segments.length === 0) {
    return null;
  }

  const textAfter = plainText.slice(lastIndex);

  if (textAfter) {
    segments.push({ type: 'text', value: textAfter });
  }

  return segments;
}

function extractMentionEntitiesFromPlainText(
  plainText: string,
): MentionEntity[] {
  const mentions: MentionEntity[] = [];

  for (const match of plainText.matchAll(
    OBJECT_CONTEXT_MENTION_IN_TEXT_PATTERN,
  )) {
    const objectKind = match[1] as ObjectContextKind;
    const labelSuffix = match[2]?.trim() ?? '';

    if (!labelSuffix) {
      continue;
    }

    const label = `${objectKind} · ${labelSuffix}`;

    mentions.push({
      entityType: 'ObjectContext',
      entityId: `plain-text:${objectKind}:${labelSuffix}`,
      label,
      extraData: {
        objectContext: {
          objectKind,
          objectId: labelSuffix,
          objectLabel: labelSuffix,
        },
      },
    });
  }

  return mentions;
}

function getRawUserMessageText({
  message,
}: {
  message: TerraUIMessage;
}): string {
  const rawText = message.parts
    .filter((part) => part.type === 'text')
    .map((part) => (part.type === 'text' ? part.text : ''))
    .join('\n');

  return stripRichCardMetadataFromMessageText(rawText);
}

function segmentsCoverAllMentions({
  segments,
  mentions,
}: {
  segments: MessageSegment[];
  mentions: MentionEntity[];
}): boolean {
  const mentionEntityIds = new Set(
    segments
      .filter((segment) => segment.type === 'mention')
      .map((segment) => segment.entityId),
  );

  return (
    mentions.length > 0 &&
    mentions.every((mention) => mentionEntityIds.has(mention.entityId))
  );
}

function buildSegmentsFromMentions({
  message,
  mentions,
}: {
  message: TerraUIMessage;
  mentions: MentionEntity[];
}): MessageSegment[] {
  return buildMentionDisplaySegments({
    plainText: getRawUserMessageText({ message }),
    positionedMentions: [],
    fallbackMentions: mentions.map((mention) => ({
      entityId: mention.entityId,
      entityType: mention.entityType,
      label: mention.label,
    })),
  });
}

export function getMentionEntitiesFromUserMessage({
  message,
}: {
  message: TerraUIMessage;
}): MentionEntity[] {
  const mentions: MentionEntity[] = [];

  for (const part of message.parts) {
    if (!isMentionsDataPart(part)) {
      continue;
    }

    mentions.push(...part.data.mentions);
  }

  if (mentions.length > 0) {
    return mentions;
  }

  return extractMentionEntitiesFromPlainText(
    getRawUserMessageText({ message }),
  );
}

export function getMentionSegmentsFromUserMessage({
  message,
}: {
  message: TerraUIMessage;
}): MessageSegment[] | null {
  for (const part of message.parts) {
    if (!isMentionsDataPart(part)) {
      continue;
    }

    const mentions = part.data.mentions ?? [];

    if (mentions.length === 0) {
      continue;
    }

    const segments = part.data.segments;

    if (
      segments &&
      segments.length > 0 &&
      segmentsCoverAllMentions({ segments, mentions })
    ) {
      return sanitizeMessageSegmentsForDisplay(segments);
    }

    const rebuiltSegments = buildSegmentsFromMentions({ message, mentions });

    if (rebuiltSegments.length > 0) {
      return sanitizeMessageSegmentsForDisplay(rebuiltSegments);
    }
  }

  const extractedSegments = extractMentionSegmentsFromPlainText(
    getRawUserMessageText({ message }),
  );

  if (!extractedSegments) {
    return null;
  }

  return sanitizeMessageSegmentsForDisplay(extractedSegments);
}

export function getUserMessageDisplayText({
  message,
}: {
  message: TerraUIMessage;
}): string {
  const segments = getMentionSegmentsFromUserMessage({ message });

  if (segments) {
    return formatMessageSegmentsAsPlainText({ segments });
  }

  return getRawUserMessageText({ message });
}
